import {useState} from 'react';
import {Button, Card, Form, Spinner} from 'react-bootstrap';
import {useIntl} from 'react-intl';
import Level3Title from '../../../../app/common/components/title/Level3Title';
import {useRunTraceroute} from '../../Hooks/RouterManager';

interface TraceroutePageProps {
  routerId: string;
}

const TraceroutePage = ({routerId}: TraceroutePageProps): JSX.Element => {
  const intl = useIntl();
  const [destination, setDestination] = useState('');
  const [validated, setValidated] = useState(false);
  const {mutate, data: hops, isLoading: loading, reset} = useRunTraceroute(routerId);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setValidated(true);
    if (!destination.trim()) return;
    reset();
    mutate(destination.trim());
  };

  return (
    <Card>
      <Card.Header>
        <Card.Title>
          <Level3Title title={intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.TRACEROUTE.TITLE'})} />
        </Card.Title>
      </Card.Header>
      <Card.Body>
        <p className="text-muted mb-4">
          {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.TRACEROUTE.SUBTITLE'})}
        </p>
        <Form noValidate validated={validated} onSubmit={handleSubmit}>
          <div className="row g-3 align-items-end">
            <div className="col-md-8">
              <Form.Group>
                <Form.Label className="required">
                  {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.TRACEROUTE.TARGET'})}
                </Form.Label>
                <Form.Control
                  type="text"
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                  placeholder="google.com ou 8.8.8.8"
                  required
                  disabled={loading}
                  isInvalid={validated && !destination.trim()}
                />
                <Form.Control.Feedback type="invalid">
                  {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.VALIDATION.REQUIRED'})}
                </Form.Control.Feedback>
              </Form.Group>
            </div>
            <div className="col-md-4">
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                    {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.TRACEROUTE.RUNNING'})}
                  </>
                ) : (
                  intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.TRACEROUTE.RUN'})
                )}
              </Button>
            </div>
          </div>
        </Form>

        {hops && (
          <div className="mt-6">
            <table className="table table-row-dashed align-middle">
              <thead>
                <tr className="fw-semibold text-muted">
                  <th>{intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.TRACEROUTE.HOP'})}</th>
                  <th>{intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.TRACEROUTE.IP'})}</th>
                  <th>{intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.TRACEROUTE.DNS'})}</th>
                  <th>{intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.TRACEROUTE.LATENCY'})}</th>
                  <th>{intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.TRACEROUTE.STATUS'})}</th>
                </tr>
              </thead>
              <tbody>
                {hops.map((h) => (
                  <tr key={h.hop}>
                    <td>{h.hop}</td>
                    <td>{h.ip}</td>
                    <td>{h.reverseDns || <span className="text-muted">—</span>}</td>
                    <td>{h.latencyMs != null ? `${h.latencyMs} ms` : '—'}</td>
                    <td>{h.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default TraceroutePage;
